(ns hai.tasks.detail
  "Full-page view of a single task: all fields untruncated, plus
   complete / edit / delete actions gated by the viewer's permissions."
  (:require
   [clojure.string :as str]
   [hai.entity.data :as entity-data]
   [hai.entity.draft :as draft]
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.tasks.ui :as ui]
   [hai.tasks.view :as view]
   [hai.ui.components :as c]
   [hai.ui.components.read-only-cue :as read-only-cue]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [hai.web-app.util.clipboard :as clipboard]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private share-icon (icons/mui-icon :outlined :person_add))

(defsub ::window-title
  [db]
  (let [task-uuid (get-in db [:routing/current-route :path-params :task/uuid])
        task (when task-uuid (get-in db [:task/uuid (uuid (str task-uuid))]))]
    (or (:task/title task) "Task")))

;; Tracks which tasks a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both of which leave a
;; nil entity in the store.
(defn draft-id
  "The draft behind one task's page. Per task, so opening a second one in
   another tab doesn't edit the first one's."
  [task-uuid]
  [::draft (uuid (str task-uuid))])

;; Seeded when the route's resolve lands and re-seeded by Cancel; the route's
;; :stop discards it.
(defevent ::seed-draft [db task-uuid]
  (draft/seed db
              (draft-id task-uuid)
              (form/from-task (get-in db [:task/uuid (uuid (str task-uuid))]))))

(defevent ::discard-draft [db task-uuid]
  (draft/discard db (draft-id task-uuid)))

(defevent ::mark-resolved [db task-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str task-uuid))))

(defsub ::resolved?
  [db task-uuid]
  (boolean (and task-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str task-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

;; The task's own mini-app card, minus the tape strip that pins a card to the
;; dashboard. The page is the surface here, so it carries the elevation a
;; dialog's overlay would.
(o/defstyled editor-card tasks-mini-app/card
  {:box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 12px 32px rgba(45,62,42,0.16)"}
  [:&::before {:display "none"}]
  [:.modal-body
   {:padding "18px 20px 20px"
    :display "flex" :flex-direction "column" :gap "16px"}])

(o/defstyled editor-column :div
  :flex :flex-col
  {:gap "16px" :max-width "760px"})

;; The stamp sits beside the composer's title the way it sits beside a board
;; row's — the offset drops it onto the title's line, past the composer's own
;; top padding.
(o/defstyled title-row :div
  :flex :items-start
  {:gap "10px"}
  [ui/stampbox {:margin-top "13px"}]
  [:.composer {:flex "1 1 auto" :min-width "0"}])

(o/defstyled field :div
  :flex :flex-col
  {:gap "6px"})

(o/defstyled field-label :div
  {:font-size "11px" :font-weight "700" :letter-spacing "0.06em"
   :text-transform "uppercase" :color t/--card-forest-70})

(o/defstyled description-body :p
  {:margin "0" :white-space "pre-wrap" :overflow-wrap "anywhere"
   :color t/--card-forest :font-size "15px" :line-height "1.6"})

(o/defstyled deadline-exact :div
  {:margin-top "4px" :font-size "13px" :color t/--card-forest-70})

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

(o/defstyled assignee :div
  :flex :items-center
  {:gap "10px"}
  [:.avatar
   :inline-flex :items-center :justify-center :flex-shrink-0
   {:width "36px" :height "36px" :border-radius "50%"
    :background t/--card-cream :color t/--card-forest
    :font-size "13px" :font-weight "700"}
   [:svg {:width "20px" :height "20px"}]
   [:&.me
    {:background "transparent"
     :box-shadow "inset 0 0 0 1px var(--task-accent)"
     :color      t/--task-accent}]]
  [:.meta :flex :flex-col {:gap "1px"}]
  [:.assignee-name {:font-size "15px" :font-weight "600" :color t/--card-forest}]
  [:.assignee-email {:font-size "13px" :color t/--card-forest-70}])

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn title->slug
  "Lower-case the title, turn spaces into dashes, and drop anything that isn't
   a letter or a dash — leaving a clean `[a-z-]` slug for the clipboard."
  [title]
  (-> (or title "")
      str/lower-case
      (str/replace #" " "-")
      (str/replace #"[^a-z0-9-]" "")))

(defn- completed-label [task]
  (str "Completed "
       (.toLocaleDateString (js/Date. (:task/completed-at task))
                            "en-US" #js {:month "short" :day "numeric" :year "numeric"})))

(defn- assignee-block [assigned-to]
  (if assigned-to
    (let [me? (data/assignee-me? @(<s :auth/identity) assigned-to)
          full-name (not-empty (:user/full-name assigned-to))
          email (data/assignee-email assigned-to)]
      [assignee
       [:span.avatar {:class (when me? "me")}
        (or (data/assignee-initials assigned-to) [icons/outlined-person])]
       [:span.meta
        [:span.assignee-name (or full-name email "Unassigned")]
        (when (and full-name email)
          [:span.assignee-email email])]])
    [muted "Unassigned"]))

(defn- task-view [task editable?]
  (r/with-let [!share-open?   (r/atom false)
               ;; Latest title, kept in sync by the render body below, so the
               ;; keydown handler (a stable ref for add/removeEventListener)
               ;; always copies the current title even after an edit.
               !title         (r/atom (:task/title task))
               copy-branch-name! (fn [e]
                                   (when (and (or (.-metaKey e) (.-ctrlKey e))
                                              (.-shiftKey e)
                                              (= (.-key e) "."))
                                     (.preventDefault e)
                                     (clipboard/copy-to-clipboard
                                      (title->slug @!title)
                                      #(>e [:toast/success {:text [:div [:b (title->slug @!title)] [:br]
                                                                   "Branch name copied to clipboard"]}]))))
               _              (.addEventListener js/window "keydown" copy-branch-name!)]
    (reset! !title (:task/title task))
    (let [task-uuid   (:task/uuid task)
          title       (:task/title task)
          description (:task/description task)
          links       (:task/activities task)
          done?       (some? (:task/completed-at task))
          can-edit?   (sharing/can-edit? task)
          can-share?  (sharing/can-share? task)
          can-delete? (sharing/owner? task)
          can-complete? (data/can-complete? @(<s :auth/identity) task)
          ;; The composer stages everything — scalars, urls, tags, linked
          ;; entities, the assignee — and `::data/save-edit` decides which
          ;; mutations that takes. The page reads the task from the entity
          ;; store, so it re-resolves once the save lands.
          save-edit (fn [payload]
                      (>e [::data/save-edit task
                           (assoc payload
                                  :success-fx [[:dispatch [:entity/resolve {:task/uuid task-uuid}]]])]))
          saving? (= :start @(<s [:fetch/state [::data/save-edit task-uuid]]))
          toggle-done (fn []
                        (>e [(if done? ::data/uncomplete ::data/complete) task-uuid]))
          close! (fn [] (>e [:routing/navigate [:tasks/view]]))
          delete! (fn []
                    (>e [::data/delete task-uuid])
                    (close!))]
      [editor-column
       ;; The owner always edits, so a viewer who cannot edit never deletes.
       (when-not can-edit?
         [read-only-cue/cue "This task is read-only"])
       [editor-card
        [header/bar {:title (if can-edit? "Editing task" "Task")}
         (when (or can-share? can-delete?)
           [kebab/menu
            {:actions        (when can-share?
                               [["Share" #(reset! !share-open? true) share-icon]])
             :on-delete      (when can-delete? delete!)
             :delete-label   "Delete task"
             :confirm-prompt "Delete this task?"
             :aria-label     "Task actions"}])
         [c/Tooltip {:content "Close"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Close"
            :on-click close!}
           [icons/outlined-close {:size 16}]]]]
        (if editable?
          [:div.modal-body
           [title-row
            [ui/stampbox {:done?      done?
                          :task-title title
                          :disabled?  (not can-complete?)
                          :on-toggle  toggle-done}]
            [:div.composer
             [form/task-form
              {:draft-id (draft-id task-uuid)
               :task task
               :start-expanded? true
               :show-hint? true
               :saving? saving?
               :on-save save-edit
               :on-cancel #(>e [::seed-draft task-uuid])
               :assignable? (data/can-assign? task)
               :render-assignee-picker
               (fn [{:keys [on-pick trigger]}]
                 [assignee-picker/component
                  {:editable? true
                   :on-pick on-pick
                   :label "Assign"
                   :trigger trigger}])}]]]]
          [:div.modal-body
           [field
            [field-label "Status"]
            (if done?
              [:span (completed-label task)]
              [muted "Open"])]
           [field
            [field-label "Deadline"]
            (if (:task/deadline-at task)
              [:div
               [view/task-deadline {:task task}]
               [deadline-exact (deadline/tooltip task)]]
              [muted "No deadline"])]
           (when (seq description)
             [field
              [field-label "Description"]
              [description-body description]])
           (when (seq links)
             [field
              [field-label "Links"]
              [view/task-links {:links links}]])
           ;; Linked contacts / notes / tasks — display-only chips for viewers;
           ;; editing them happens through the composer, which is the whole page
           ;; for anyone with edit rights.
           (when (or (seq (:task/contacts task))
                     (seq (:task/notes task))
                     (seq (:task/tasks task)))
             [field
              [field-label "Linked"]
              [sections/entity-extras
               {:entity-uuid    task-uuid
                :link-urls      []
                :contacts       (:task/contacts task)
                :linked-notes   (:task/notes task)
                :linked-tasks   (:task/tasks task)
                ;; The task's own page — nothing here to press open, so every
                ;; chip shows rather than standing behind a "+N".
                :expanded?      true
                ;; Clicking a chip routes to that entity's own page — reuse the
                ;; note surface's shared routing (contact→edit w/ return-to,
                ;; task→detail, note→detail).
                :on-open-entity entity-data/open-entity}]])
           [field
            [field-label "Assigned to"]
            [assignee-block (:task/assigned-to task)]]])]
       [sharing.components/dialog
        {:entity      task
         :description "People with access to this task"
         :!open?      !share-open?
         :on-closed   #(>e [:entity/resolve {:task/uuid task-uuid}])}]])
    (finally
      (.removeEventListener js/window "keydown" copy-branch-name!))))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        task      @(<s [:entity params])
        resolved? @(<s [::resolved? (:task/uuid params)])
        editable? (and task (sharing/can-edit? task))]
    ;; No breadcrumbs or page heading: the card's own header names the task and
    ;; its ✕ is the way back, the same as the note page.
    [layout/page
     [layout/content
      (cond
        ;; Nothing of the task renders until the resolve lands. Arriving from a
        ;; list, the entity store already holds it, so without this the
        ;; read-only layout shows first — handing an editor a Linked adder wired
        ;; to a page that is about to be replaced by the composer.
        (not resolved?) [loading-state [c/Spinner {:size "3"}] "Loading…"]
        ;; The composer seeds itself from `initial` once, at mount. Keying on
        ;; `:task/updated-at` — which only the server moves — remounts it when a
        ;; resolve lands, so the next save diffs against what the task now has
        ;; rather than re-sending the tags and links it staged before the last one.
        task ^{:key (str (:task/uuid task) "@" (:task/updated-at task))}
        [task-view task editable?]
        :else [muted "This task doesn't exist or you no longer have access to it."])]]))
